import { Globe01 } from '@untitledui/icons';

import { GmailIcon, LinkedInIcon } from '@/lib/crm/utils/icons/CustomIcons';
import { isAllowedUrl } from '@/lib/crm/utils/urlValidation';
import { getPropertyValue } from '@/types/crm';

import type { CrmObject } from '../../core/types';
import type { HeaderActionDefinition } from './entityTypeRegistry';

/** First non-empty email address on a contact, or null. */
function getContactEmail(object: CrmObject): string | null {
  const raw = getPropertyValue<string | string[]>(
    object.properties?.ahoy_email
  );
  const candidates = Array.isArray(raw) ? raw : [raw];
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return null;
}

/** https://-prefixed website URL derived from a company's ahoy_domain, or null. */
function getCompanyWebsiteUrl(object: CrmObject): string | null {
  const raw = getPropertyValue<string>(object.properties?.ahoy_domain);
  if (typeof raw !== 'string') {
    return null;
  }
  const domain = raw.trim();
  if (!domain) {
    return null;
  }
  // Detect an actual scheme (e.g. `https://`), not just an `http` prefix —
  // `startsWith('http')` would falsely treat domains like `httpbin.org` as
  // already prefixed and skip the `https://` scheme, breaking the link.
  return /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
}

export const gmailAction: HeaderActionDefinition = {
  id: 'gmail',
  icon: GmailIcon,
  getHref: object => {
    const email = getContactEmail(object);
    if (!email) {
      return null;
    }
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;
  },
  getLabel: () => 'Send email via Gmail',
};

export const linkedinAction: HeaderActionDefinition = {
  id: 'linkedin',
  icon: LinkedInIcon,
  getHref: object => {
    const url = getPropertyValue<string>(object.properties?.ahoy_linkedin_url);
    if (typeof url !== 'string' || !url.trim() || !isAllowedUrl(url)) {
      return null;
    }
    return url;
  },
  getLabel: () => 'View LinkedIn profile',
};

export const websiteAction: HeaderActionDefinition = {
  id: 'website',
  icon: Globe01,
  getHref: object => getCompanyWebsiteUrl(object),
  getLabel: () => 'Visit company website',
  guard: object => {
    const url = getCompanyWebsiteUrl(object);
    return url !== null && isAllowedUrl(url);
  },
};
